/* eslint-disable no-unused-vars */
import { useRef, useState } from "react";
import { motion } from "framer-motion";
import Swal from "sweetalert2";

import { sendEmail } from "../services/emailService";
import { trackContactFormSubmit } from "../services/trackingService";
import { styles } from "../styles";
import { EarthCanvas } from "./canvas";
import CountryPhoneField from "./ui/CountryPhoneField";
import { SectionWrapper } from "../hoc";
import { slideIn } from "../utils/motion";
import { DEFAULT_COUNTRY } from "../constants/countries";

const PROJECT_TYPES = [
  "Sistema de Gestión",
  "Aplicación Web",
  "Aplicación Móvil",
  "APIs e Integraciones",
  "Automatización e IA",
  "Consultoría Profesional",
  "Cloud & DevOps",
  "Otro",
];

const ContactSection = () => {
  const isMobile = window.innerWidth <= 768;
  const formRef = useRef();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    projectType: PROJECT_TYPES[0],
    message: "",
  });
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);

  const maxTextAreaLength = 500;
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const fieldClass = `input-glass-bg py-2.5 px-4 placeholder:text-secondary rounded-lg outline-none border border-white/10 focus:border-sky-400/50 focus:shadow-[0_0_0_2px_rgba(56,189,248,0.12)] transition-all duration-200 ${styles.contactText}`;

  const handleChange = (e, maxLength = 50) => {
    const { target } = e;
    const { name, value } = target;
    if (value.length <= maxLength) {
      setForm((prevForm) => ({
        ...prevForm,
        [name]: value,
      }));

      if (errors[name]) {
        setErrors((prevErrors) => {
          const nextErrors = { ...prevErrors };
          delete nextErrors[name];
          return nextErrors;
        });
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!form.name.trim()) {
      errors.name = "Por favor, ingresá tu nombre";
      isValid = false;
    }

    if (!form.email.trim()) {
      errors.email = "Por favor, ingresá tu email";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      errors.email = "Por favor, ingresá un email válido";
      isValid = false;
    }

    if (!form.message.trim()) {
      errors.message = "Por favor, ingresá tu mensaje";
      isValid = false;
    }

    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) {
      errors.phone = "Por favor, ingresá un teléfono válido";
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await sendEmail({
        name: form.name,
        email: form.email,
        phone: `${selectedCountry.value} ${form.phone}`,
        projectType: form.projectType,
        message: form.message,
      });

      trackContactFormSubmit();
      await Swal.fire({
        title: "¡Enviado!",
        text: "Te responderé lo más pronto posible",
        icon: "success",
        color: "var(--color-foreground)",
        confirmButtonColor: "var(--color-tertiary)",
        background: "var(--color-black-200)",
        customClass: {
          content: "text-foreground",
        },
      });

      setForm({
        name: "",
        email: "",
        phone: "",
        projectType: PROJECT_TYPES[0],
        message: "",
      });
      setSelectedCountry(DEFAULT_COUNTRY);
    } catch (error) {
      console.error(error);

      await Swal.fire({
        title: "Error",
        text: "Algo salió mal. Por favor, inténtalo de nuevo.",
        icon: "error",
        color: "var(--color-foreground)",
        confirmButtonColor: "var(--color-tertiary)",
        background: "var(--color-black-200)",
        customClass: {
          content: "text-foreground",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`xl:mt-4 flex xl:flex-row flex-col-reverse gap-4 overflow-hidden xl:items-center`}
    >
      <motion.div
        variants={slideIn("left", "tween", 0.2, 1)}
        className="contact-card-surface flex-1 glass glass-card p-4 sm:p-6 rounded-2xl"
      >
        <p className={styles.sectionSubText}>¿Tenés un proyecto en mente?</p>
        <h3 className={`${styles.sectionHeadText}`}>Contacto</h3>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className={`mt-4 flex flex-col gap-3 ${loading ? "pointer-events-none" : ""}`}
          aria-busy={loading}
        >
          <fieldset disabled={loading} className="contents">
          <label className="flex flex-col">
            <span className="text-foreground font-medium mb-1">Tu nombre</span>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              maxLength={100}
              placeholder="¿Cuál es tu nombre o el de tu empresa?"
              autoComplete="off"
              className={fieldClass}
            />
            {errors.name && <span className="mt-1 text-red-400 text-sm">{errors.name}</span>}
          </label>
          <label className="flex flex-col">
            <span className="text-foreground font-medium mb-1">Tu email</span>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              maxLength={100}
              placeholder="¿Dónde te envío mi respuesta?"
              autoComplete="off"
              className={fieldClass}
            />
            {errors.email && (
              <span className="mt-1 text-red-400 text-sm">{errors.email}</span>
            )}
          </label>
          <label className="flex flex-col">
            <span className="text-foreground font-medium mb-1">Tu teléfono</span>
            <CountryPhoneField
              country={selectedCountry}
              onCountryChange={setSelectedCountry}
              phone={form.phone}
              onPhoneChange={(e) => handleChange(e, 100)}
              fieldClass={fieldClass}
              disabled={loading}
            />
              {errors.phone && <span className="mt-1 text-red-400 text-sm">{errors.phone}</span>}
          </label>
          <label className="flex flex-col">
            <span className="text-foreground font-medium mb-1">Tipo de proyecto</span>
            <select
              name="projectType"
              value={form.projectType}
              onChange={(e) => handleChange(e, 100)}
              className={`${fieldClass} cursor-pointer appearance-none`}
            >
              {PROJECT_TYPES.map((type) => (
                <option key={type} value={type} className="bg-black-200 text-foreground">
                  {type}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col">
            <span className="text-foreground font-medium mb-1">Contame sobre tu proyecto</span>
            <textarea
              rows={3}
              name="message"
              value={form.message}
              onChange={(e) => handleChange(e, maxTextAreaLength)}
              placeholder="¿Qué necesitás? Ej: una página web, una aplicación web, una aplicación móvil, etc..."
              className={`${fieldClass} resize-none`}
              style={{
                height: "110px",
              }}
            />
            <span className="mt-1 flex justify-end text-secondary text-sm">
              {form.message.length}/{maxTextAreaLength}
            </span>
            {errors.message && (
              <span className="mt-1 text-red-400 text-sm">{errors.message}</span>
            )}
          </label>

          <motion.button
            initial={{ opacity: 0.6 }}
            whileTap={{ scale: 0.9 }}
            whileInView={{ opacity: 1 }}
            type="submit"
            className="bg-gradient-to-r from-sky-700 to-cyan-600 text-white font-bold py-2.5 px-8 rounded-full shadow-lg shadow-sky-900/25 hover:from-sky-600 hover:to-cyan-500 hover:shadow-xl transition-all duration-300 ease-in-out"
            disabled={loading}
          >
            {loading ? "Enviando..." : "Enviar Mensaje"}
          </motion.button>
          </fieldset>
        </form>
      </motion.div>
      {!isMobile && (
        <motion.div
          variants={slideIn("right", "tween", 0.2, 1)}
          className="xl:flex-1 xl:h-[550px] md:h-[450px] h-[350px] flex items-center justify-center"
        >
          <EarthCanvas />
        </motion.div>
      )}
    </div>
  );
};

const Contact = SectionWrapper(ContactSection, "contacto", 0.2, "sm:px-16 px-6 sm:py-8 py-6");
export default Contact;
